<template>
  <div class="file-uploader">
    <file-pond
      ref="pond"
      name="file"
      :credits="false"
      :allow-multiple="true"
      :allow-revert="true"
      :max-file-size="maxFileSize"
      :label-idle="labelIdle"
      :server="server"
      @processfile="onProcessFile"
      @removefile="onRemoveFile"
    />
  </div>
</template>

<script>
import vueFilePond from 'vue-filepond'
import 'filepond/dist/filepond.min.css'
import FilePondPluginFileValidateSize from 'filepond-plugin-file-validate-size'

import { presignUpload, deleteUpload } from '../services/api.js'
import { t, localeState } from '../i18n/index.js'

const FilePond = vueFilePond(FilePondPluginFileValidateSize)

// Keep in sync with MAX_UPLOAD_MB in server/.env (client-side guard).
const MAX_UPLOAD_MB = 200

export default {
  name: 'FileUploader',
  components: { FilePond },

  props: {
    // Survey session token — the S3 folder name and upload authorization.
    token: { type: String, required: true }
  },

  emits: ['update:files'],

  data () {
    return {
      uploaded: []
    }
  },

  computed: {
    maxFileSize () {
      return `${MAX_UPLOAD_MB}MB`
    },
    labelIdle () {
      // localeState.current keeps this reactive to the language toggle
      void localeState.current
      return t({
        fr: 'Glissez vos fichiers ici ou <span class="filepond--label-action">parcourez</span>',
        en: 'Drag & drop your files or <span class="filepond--label-action">Browse</span>'
      })
    },
    server () {
      const token = this.token
      return {
        // Custom process: presign → PUT directly to S3.
        process: (fieldName, file, metadata, load, error, progress, abort) => {
          let xhr
          presignUpload(token, file.name, file.type || 'application/octet-stream', file.size)
            .then(({ url, headers, key }) => {
              xhr = new XMLHttpRequest()
              xhr.open('PUT', url)
              Object.entries(headers || {}).forEach(([k, v]) => xhr.setRequestHeader(k, v))
              xhr.upload.onprogress = (e) => progress(e.lengthComputable, e.loaded, e.total)
              xhr.onload = () => {
                if (xhr.status >= 200 && xhr.status < 300) {
                  load(key) // serverId = S3 object key
                } else {
                  error(t({ fr: 'Échec du téléversement', en: 'Upload failed' }))
                }
              }
              xhr.onerror = () => error(t({ fr: 'Erreur réseau', en: 'Network error' }))
              xhr.send(file)
            })
            .catch((err) => error(err.message || 'presign error'))

          return {
            abort: () => {
              if (xhr) xhr.abort()
              abort()
            }
          }
        },
        // Revert: delete the object the user removed after uploading.
        revert: (key, load, error) => {
          deleteUpload(token, key)
            .then(() => load())
            .catch(() => error(t({ fr: 'Échec de la suppression', en: 'Revert failed' })))
        }
      }
    }
  },

  methods: {
    onProcessFile (error, file) {
      if (error) return
      this.uploaded.push({
        key: file.serverId,
        filename: file.filename,
        size: file.fileSize,
        type: file.fileType
      })
      this.$emit('update:files', this.uploaded)
    },
    onRemoveFile (error, file) {
      if (error) return
      this.uploaded = this.uploaded.filter((f) => f.key !== file.serverId)
      this.$emit('update:files', this.uploaded)
    }
  }
}
</script>

<style scoped>
.file-uploader {
  margin: 10px 0 20px 0;
}

:deep(.filepond--panel-root) {
  background-color: var(--aecom-earth-green-25, #cfdfd7);
  border: 2px dashed var(--aecom-earth-green, #008768);
}

:deep(.filepond--drop-label) {
  color: var(--aecom-midnight-teal, #00353e);
}

:deep(.filepond--label-action) {
  color: var(--aecom-earth-green, #008768);
  text-decoration-color: var(--aecom-earth-green, #008768);
}

:deep(.filepond--item-panel) {
  background-color: var(--aecom-midnight-teal, #00353e);
}
</style>
